import { useState } from 'react';
import { ChevronDown, ChevronUp, UserPlus } from 'lucide-react';
import type { OpportunityCluster } from '../../types';

interface HireRecommendationPanelProps {
  clusters: OpportunityCluster[];
}

const RANK_COLORS = ['border-red-500', 'border-orange-500', 'border-amber-500', 'border-yellow-500', 'border-lime-500'];

export default function HireRecommendationPanel({ clusters }: HireRecommendationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const top5 = clusters.slice(0, 5);

  if (top5.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Recommended Next Hires</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">({top5.length})</span>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {top5.map((cluster, i) => (
            <div
              key={cluster.id}
              className={`border-l-4 ${RANK_COLORS[i]} bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">#{cluster.rank}</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{cluster.label}</span>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <div>
                  Score: <span className="font-bold text-orange-600 dark:text-orange-400">{cluster.opportunityScore}</span>
                </div>
                <div>{cluster.totalSchools.toLocaleString()} total schools</div>
                <div>{cluster.openCount.toLocaleString()} open &middot; {cluster.penetrationPct}% penetration</div>
                <div className="pt-1 border-t border-gray-200 dark:border-gray-600 mt-1">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Suggested territory:</span>
                  <br />
                  {cluster.suggestedTerritory.zipPrefixes.length > 0 ? (
                    <>
                      {cluster.suggestedTerritory.zipPrefixes.length} ZIP prefixes covering ~{cluster.suggestedTerritory.estimatedSchools} schools
                      {cluster.suggestedTerritory.nearestExistingTerritory && (
                        <div className="mt-0.5 text-gray-400">
                          Near: {cluster.suggestedTerritory.nearestExistingTerritory}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400">All ZIPs already assigned</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
